import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';

import {
  CLOUD_STORAGE_FILES_BY_PATH_URL,
  CLOUD_STORAGE_FOLDERS_URL,
} from '@/consts/urls';
import { getContext } from '@/context';
import getAuthJsonHeaders from '@/requests/common/getAuthJsonHeaders';
import getCurrentUserId from '@/utils/user/getCurrentUserId';

type FolderResponse = {
  response?: { uid?: string } | Array<{ uid?: string }>;
};

const readUid = (payload: FolderResponse): string | null => {
  const { response } = payload;
  const folder = Array.isArray(response) ? response[0] : response;

  return folder?.uid ?? null;
};

const findByPath = async (folderName: string): Promise<string | null> => {
  const response = await getContext().utils.api.fetch(
    `${CLOUD_STORAGE_FILES_BY_PATH_URL}?path=${encodeURIComponent(folderName)}`,
    { headers: getAuthJsonHeaders() }
  );

  if (!response.ok) return null;

  return readUid(await response.json());
};

const create = async (folderName: string): Promise<string | null> => {
  const response = await getContext().utils.api.fetch(
    CLOUD_STORAGE_FOLDERS_URL,
    {
      method: 'POST',
      headers: getAuthJsonHeaders(),
      body: JSON.stringify({ name: folderName }),
    }
  );

  if (!response.ok) return null;

  return readUid(await response.json());
};

const resolve = async (folderName: string): Promise<string | null> => {
  try {
    return (await findByPath(folderName)) ?? (await create(folderName));
  } catch (error) {
    errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN);

    return null;
  }
};

const pendingByFolder = new Map<string, Promise<string | null>>();

/**
 * The uid of the tool's Drive folder, created on first use — named alone it is
 * not matched against the folder the backend already writes into, and Drive
 * ends up with a second folder of the same name.
 *
 * Looked up once and shared by concurrent callers: every result of a
 * multi-output run asks at the same moment. Keyed by user and folder, neither
 * of which is fixed for the session (`projectJsonHandler` sets its own folder;
 * the portfolio surface signs out without a reload). A `null` answer is not
 * cached, so the next save retries instead of falling back to the root.
 */
const getDriveFolderUid = (folderName: string): Promise<string | null> => {
  // A project that names no folder gets none, rather than one called
  // "undefined": `projectJsonHandler` writes whatever its JSON carries.
  if (!folderName) return Promise.resolve(null);

  const key = `${getCurrentUserId()}:${folderName}`;

  const pending = pendingByFolder.get(key);
  if (pending) return pending;

  const lookup = resolve(folderName).then((uid) => {
    if (!uid) pendingByFolder.delete(key);

    return uid;
  });
  pendingByFolder.set(key, lookup);

  return lookup;
};

export default getDriveFolderUid;
